import { Suspense, lazy } from 'react'
import type { ContentBlock } from '@/types/content'
import { RichTextView } from './RichTextView'
import styles from './BlockRenderer.module.css'

// KaTeX and its fonts are ~260 kB and only one project uses an equation, so
// the renderer is split into its own chunk and fetched on demand.
const MathView = lazy(() => import('./MathView'))

/**
 * The one place a `ContentBlock` becomes markup.
 *
 * Add a variant to `ContentBlock` and TypeScript will fail the `never` check at
 * the bottom of this switch until a case is added here.
 */
function Block({ block }: { block: ContentBlock }) {
  switch (block.kind) {
    case 'text':
      return (
        <p className={styles.paragraph}>
          <RichTextView value={block.content} />
        </p>
      )

    case 'heading': {
      const Tag = block.level === 4 ? 'h4' : 'h3'
      return <Tag className={styles.heading}>{block.text}</Tag>
    }

    case 'image':
      return (
        <figure className={styles.figure} style={{ maxWidth: block.maxWidth }}>
          <img src={block.src} alt={block.alt} loading="lazy" decoding="async" />
          {block.caption && <figcaption>{block.caption}</figcaption>}
        </figure>
      )

    case 'gallery':
      return (
        <div
          className={styles.gallery}
          data-columns={block.columns ?? 'auto'}
          role="group"
        >
          {block.images.map((image) => (
            <figure key={image.src} className={styles.galleryItem}>
              <img src={image.src} alt={image.alt} loading="lazy" decoding="async" />
              {image.caption && <figcaption>{image.caption}</figcaption>}
            </figure>
          ))}
        </div>
      )

    case 'list': {
      const Tag = block.ordered ? 'ol' : 'ul'
      return (
        <div className={styles.listWrap}>
          {block.title && <p className={styles.listTitle}>{block.title}</p>}
          <Tag className={styles.list}>
            {block.items.map((item, i) => (
              <li key={i}>
                <RichTextView value={item} />
              </li>
            ))}
          </Tag>
        </div>
      )
    }

    case 'pdf':
      return (
        <figure className={styles.embed}>
          <iframe
            src={block.src}
            title={block.title}
            height={block.height ?? '640'}
            loading="lazy"
          />
          <figcaption>
            <a href={block.src} target="_blank" rel="noopener noreferrer">
              Open “{block.title}” in a new tab ↗
            </a>
          </figcaption>
        </figure>
      )

    case 'video':
      return (
        <figure className={styles.embed} data-aspect="video">
          {block.provider === 'youtube' ? (
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${block.src}`}
              title={block.title}
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <video src={block.src} controls preload="metadata" />
          )}
        </figure>
      )

    case 'quote':
      return (
        <blockquote className={styles.quote}>
          <p>
            <RichTextView value={block.content} />
          </p>
          {block.attribution && <cite>{block.attribution}</cite>}
        </blockquote>
      )

    case 'note':
      return (
        <aside className={styles.note} data-tone={block.tone ?? 'info'}>
          {block.title && <p className={styles.noteTitle}>{block.title}</p>}
          <p>
            <RichTextView value={block.content} />
          </p>
        </aside>
      )

    case 'math':
      return (
        <div className={styles.mathBlock}>
          <Suspense fallback={<code>{block.latex}</code>}>
            <MathView
              latex={block.latex}
              display={block.display ?? true}
              {...(block.description === undefined ? {} : { description: block.description })}
            />
          </Suspense>
        </div>
      )

    case 'specs':
      return (
        <dl className={styles.specs}>
          {block.items.map((item) => (
            <div key={item.label} className={styles.specRow}>
              <dt>{item.label}</dt>
              <dd>{item.value}</dd>
            </div>
          ))}
        </dl>
      )

    default: {
      // Exhaustiveness guard: a new block kind breaks the build here.
      const exhaustive: never = block
      if (import.meta.env.DEV) {
        console.error('[content] Unhandled block kind:', exhaustive)
      }
      return null
    }
  }
}

export function BlockRenderer({ blocks }: { blocks: ContentBlock[] }) {
  return (
    <div className={styles.prose}>
      {blocks.map((block, i) => (
        <Block key={i} block={block} />
      ))}
    </div>
  )
}
